import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, DownloadIcon, ExternalLinkIcon, FileTextIcon, MailIcon, PhoneIcon, ClockIcon, GlobeIcon } from 'lucide-react';
import { SEWP_LOGO, ORDERING_GUIDE_URL, sewpCategories, contractInfo, programSupportContacts, pmContacts, fairOpportunityIntro, fairOpportunityList, fairOpportunityRest, orderingSupport, orderingGuideContents } from '../data/sewpVI';
const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nrs-orange focus-visible:ring-offset-2 rounded-sm';
const linkClass = `font-semibold text-nrs-navy underline underline-offset-2 hover:text-nrs-orange ${focusRing}`;
function ExternalLink({
  href,
  children
}: {
  href: string;
  children: React.ReactNode;
}) {
  return <a href={href} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1 break-all ${linkClass}`} data-id="element-1873" data-mp="1bcv4bkhhrjqa">
      {children}
      <ExternalLinkIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" data-id="element-1874" data-mp="35o076mkr7s4k" />
      <span className="sr-only" data-id="element-1875" data-mp="3u3mtrsbsj53j">(opens in new tab)</span>
    </a>;
}
function SectionHeading({
  id,
  children
}: {
  id: string;
  children: React.ReactNode;
}) {
  return <>
      <h2 id={id} className="text-2xl md:text-3xl font-bold mb-3" data-id="element-1876" data-mp="7otwst43ol1e">
        {children}
      </h2>
      <div className="h-1 w-12 bg-nrs-orange rounded-full mb-6" aria-hidden="true" data-id="element-1877" data-mp="2pqrr8o4eetf"></div>
    </>;
}
export function NasaSewpPage() {
  return <div className="w-full" data-id="element-1878" data-mp="3b6lkvwf48kub">
      {/* HERO */}
      <section className="section-navy hero-grid-pattern" data-id="element-1879" data-mp="3b7dhnakg8om6">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1880" data-mp="367r4100pl6cu">
          <nav aria-label="Breadcrumb" className="mb-6" data-id="element-1881" data-mp="3k6qkvlhzcgyg">
            <ol className="flex flex-wrap items-center gap-1 text-sm font-bold text-nrs-light" data-id="element-1882" data-mp="3k44ehp6ts7ht">
              {[{
              to: '/',
              label: 'Home'
            }, {
              to: '/about-us',
              label: 'About Us'
            }, {
              to: '/about-us/contract-vehicles',
              label: 'Contract Vehicles'
            }].map(crumb => <li key={crumb.to} className="flex items-center gap-1" data-id="element-1883" data-mp="3o83d1d29wflb">
                  <Link to={crumb.to} className="hover:text-white underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-sm" data-id="element-1884" data-mp="3t76iuzq2upuc">
                    {crumb.label}
                  </Link>
                  <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1885" data-mp="62guooxlq3d7" />
                </li>)}
              <li aria-current="page" className="text-white" data-id="element-1886" data-mp="35k83k5dp1ny0">
                NASA SEWP VI
              </li>
            </ol>
          </nav>

          <div className="flex flex-col sm:flex-row sm:items-center gap-6" data-id="element-1887" data-mp="3ejbm988y81bz">
            <img src={SEWP_LOGO} alt="NASA SEWP VI Contract Holder logo" className="h-24 w-36 flex-shrink-0 object-contain rounded-md bg-white p-1" data-id="element-1888" data-mp="3eh8og21k13wp" />
            <div data-id="element-1889" data-mp="2mqujwwffl3xp">
              <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-3" data-id="element-1890" data-mp="2mrdyc35vha6z">
                NASA SEWP VI
              </h1>
              <p className="text-lg text-nrs-light font-bold" data-id="element-1891" data-mp="2qr4n61bu9g8v">
                Prime contract holder — Categories B &amp; C
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* A. CONTRACT OVERVIEW */}
      <section className="section-white" aria-labelledby="overview" data-id="element-1892" data-mp="2a8ici2pd3za6">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1893" data-mp="25a731t2zbqsh">
          <div className="max-w-3xl" data-id="element-1894" data-mp="25cb5e6l8chqn">
            <SectionHeading id="overview" data-id="element-1895" data-mp="20bnfjo8qm6y0">Contract Overview</SectionHeading>
            <p className="text-lg text-nrs-gray leading-relaxed mb-8" data-id="element-1896" data-mp="20cyg7f6vyykw">
              NASA Solutions for Enterprise-Wide Procurement (SEWP, pronounced
              "soup") VI is a multi-award Government-Wide Acquisition Contract
              (GWAC) that enables NASA and all Federal Agencies (and their
              approved contractors) to acquire Information Technology,
              Communication, and Audio-Visual (ITC/AV) solutions and services.
              Created in 1993, SEWP was the first GWAC in the federal
              acquisition space and is self-funded through a 0.34% usage fee.
            </p>
            <p className="text-nrs-gray leading-relaxed mb-5" data-id="element-1897" data-mp="3hmm254p5oett">
              SEWP VI is organized into service categories. New River Systems
              holds prime awards in two:
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1898" data-mp="37wamikw40h73">
            {sewpCategories.map(cat => <div key={cat.code} className="card border-l-4 border-l-nrs-orange" data-id="element-1899" data-mp="2twrq2pcnuuam">
                <p className="text-sm font-bold text-nrs-navy mb-1" data-id="element-1900" data-mp="2xymqzsqoj5nv">{cat.code}</p>
                <h3 className="text-xl font-bold mb-2" data-id="element-1901" data-mp="2xwjs0j7ju270">{cat.name}</h3>
                <p className="text-nrs-gray leading-relaxed" data-id="element-1902" data-mp="2k0ofqw7nnpcl">{cat.description}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* B. CONTRACT INFORMATION */}
      <section className="section-light-blue" aria-labelledby="contract-info" data-id="element-1903" data-mp="i8s13dq6xzdl">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1904" data-mp="n73dnw7venzi">
          <SectionHeading id="contract-info" data-id="element-1905" data-mp="27ovse4wjuti7">NRS Contract Information</SectionHeading>
          <div className="card" data-id="element-1906" data-mp="27mjbzw6fwr79">
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-10" data-id="element-1907" data-mp="22qazkqjmnc3s">
              {contractInfo.slice(0, 1).map(item => <div key={item.label} className="py-4 border-b border-gray-200 md:col-span-2" data-id="element-1908" data-mp="2hexdcntlvvsw">
                  <dt className="text-sm font-bold text-nrs-navy mb-1" data-id="element-1909" data-mp="2cnp4pu03oegs">{item.label}</dt>
                  <dd className="text-nrs-gray" data-id="element-1910" data-mp="2qeu9faibrwxz">{item.value}</dd>
                </div>)}
              <div className="py-4 border-b border-gray-200 md:col-span-2" data-id="element-1911" data-mp="2lgajz04iw30v">
                <dt className="text-sm font-bold text-nrs-navy mb-3" data-id="element-1912" data-mp="2lid9zk5odw5l">Program Support Contacts</dt>
                <dd data-id="element-1913" data-mp="30bgrjrj9o9wb">
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4" data-id="element-1914" data-mp="2vegkpeelf345">
                    {programSupportContacts.map(c => <li key={c.role} data-id="element-1915" data-mp="15z0hsgr9idmp">
                        <p className="font-bold text-nrs-navy" data-id="element-1916" data-mp="15zsbfm1au1ac">{c.role}</p>
                        <p className="text-nrs-gray" data-id="element-1917" data-mp="111h0t936jmr2">{c.name}</p>
                        <p className="text-nrs-gray" data-id="element-1918" data-mp="1qoujksfzizcz">
                          <a href={`tel:${c.phoneHref}`} className={linkClass} data-id="element-1919" data-mp="1qq5qv28n2p8u">
                            {c.phone}
                          </a>
                          <span aria-hidden="true" data-id="element-1920" data-mp="1zpixzo9aj1qe"> | </span>
                          <a href={`mailto:${c.email}`} className={`${linkClass} break-all`} data-id="element-1921" data-mp="1zny6y8m6q0xa">
                            {c.email}
                          </a>
                        </p>
                      </li>)}
                  </ul>
                </dd>
              </div>
              {contractInfo.slice(1).map(item => <div key={item.label} className="py-4 border-b border-gray-200" data-id="element-1922" data-mp="2dmo1phlj7oih">
                  <dt className="text-sm font-bold text-nrs-navy mb-1" data-id="element-1923" data-mp="2igist63gv5rl">{item.label}</dt>
                  <dd className="text-nrs-gray" data-id="element-1924" data-mp="2nebze08om1se">{item.value}</dd>
                </div>)}
              <div className="py-4 md:col-span-2" data-id="element-1925" data-mp="2sf81q9ddxfvp">
                
                
              </div>
            </dl>
          </div>
        </div>
      </section>

      {/* C. PROGRAM MANAGEMENT CONTACTS */}
      

      {/* D. FAIR OPPORTUNITY */}
      <section className="section-light-blue" aria-labelledby="fair-opportunity" data-id="element-1926" data-mp="gql1n3nj640j">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1927" data-mp="gso3cky1pll3">
          <div className="max-w-3xl" data-id="element-1928" data-mp="bs0cq0dqb6re">
            <SectionHeading id="fair-opportunity" data-id="element-1929" data-mp="6sf1orlab7yb">
              Fair Opportunity and Requests for Quotes
            </SectionHeading>
            <div className="space-y-4 text-nrs-gray leading-relaxed" data-id="element-1930" data-mp="14gfevmbrhulb">
              {fairOpportunityIntro.map(p => <p key={p} data-id="element-1931" data-mp="zi43v89qde1i">{p}</p>)}
              <ol className="list-decimal list-outside pl-6 space-y-2" data-id="element-1932" data-mp="zhumua6nyh7y">
                {fairOpportunityList.map(item => <li key={item} data-id="element-1933" data-mp="uo9o2fx8xj7p">{item}</li>)}
              </ol>
              {fairOpportunityRest.map(p => <p key={p} data-id="element-1934" data-mp="pqzqgczpe18s">{p}</p>)}
            </div>
          </div>
        </div>
      </section>

      {/* E. ORDERING & SUPPORT */}
      <section className="section-white" aria-labelledby="ordering-support" data-id="element-1935" data-mp="1ifx8qg3wzy1j">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1936" data-mp="1idu87254ymik">
          <SectionHeading id="ordering-support" data-id="element-1937" data-mp="qqsu98zawi60">Ordering &amp; Support</SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1938" data-mp="vk413a444yxx">
            {orderingSupport.map(item => <div key={item.title} className="card flex flex-col" data-id="element-1939" data-mp="hsql265eyj7n">
                <h3 className="text-xl font-bold mb-3" data-id="element-1940" data-mp="mqio9wbidrr5">{item.title}</h3>
                <p className="text-nrs-gray leading-relaxed mb-4" data-id="element-1941" data-mp="monzu8113hib">{item.body}</p>
                <p className="text-sm font-bold text-nrs-navy mt-auto" data-id="element-1942" data-mp="1ah4lesjz5rmd">{item.contact}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* F. ORDERING GUIDE */}
      <section className="section-light-blue" aria-labelledby="ordering-guide" data-id="element-1943" data-mp="10hdqwqvezcma">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1944" data-mp="15ggvybbe9iu9">
          <SectionHeading id="ordering-guide" data-id="element-1945" data-mp="3l8nxymuym59o">NRS SEWP VI Ordering Guide</SectionHeading>
          <div className="card flex flex-col md:flex-row md:items-center gap-8" data-id="element-1946" data-mp="3q7hd0jpwa4by">
            <div className="flex items-start gap-4 flex-grow" data-id="element-1947" data-mp="3q5eetcel94w5">
              <FileTextIcon className="h-10 w-10 text-nrs-navy flex-shrink-0" aria-hidden="true" data-id="element-1948" data-mp="3imwwqhsnchrk" />
              <div data-id="element-1949" data-mp="3dntx5cvfr3qz">
                <h3 className="text-xl font-bold mb-3" data-id="element-1950" data-mp="3dolq0fxnendk">NRS SEWP VI Ordering Guide (PDF)</h3>
                <p className="text-sm font-bold text-nrs-navy mb-2" data-id="element-1951" data-mp="3ro4n8dox8eb3">Contents:</p>
                <ul className="list-disc list-outside pl-5 text-nrs-gray space-y-1" data-id="element-1952" data-mp="3mqmn9csvt9f8">
                  {orderingGuideContents.map(c => <li key={c} data-id="element-1953" data-mp="2z0ihays80xl2">{c}</li>)}
                </ul>
              </div>
            </div>
            <a href="https://jobs.newriversystems.com/wp-content/uploads/2026/10/New-River-Systems-SEWP-VI-Ordering-Guide.pdf" target="_blank" className={`btn-primary inline-flex items-center justify-center gap-2 flex-shrink-0 ${focusRing}`} data-id="element-1954" data-mp="2uzym40r7k41k">
              <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-1955" data-mp="2g9hlgjmedi5q" />
              Download the Ordering Guide
            </a>
          </div>
        </div>
      </section>

      {/* G. CONTACT NASA SEWP */}
      <section className="section-white" aria-labelledby="contact-sewp" data-id="element-1956" data-mp="2ti7gyw4taqlx">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1957" data-mp="2tgd2o0o1pwqt">
          <SectionHeading id="contact-sewp" data-id="element-1958" data-mp="2ojw6qkalue36">How to Contact NASA SEWP</SectionHeading>
          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1959" data-mp="2jj8drt2tfn6b">
            <div className="flex gap-3" data-id="element-1960" data-mp="2fjg4tf9iff0f">
              <ClockIcon className="h-5 w-5 text-nrs-navy mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-1961" data-mp="2fietd9ol0qi6" />
              <div data-id="element-1962" data-mp="2akwj9fytw58l">
                <dt className="font-bold text-nrs-navy" data-id="element-1963" data-mp="2alep94balxtp">SEWP PMO Hours</dt>
                <dd className="text-nrs-gray" data-id="element-1964" data-mp="25rk4rynv5etk">Mon–Fri, 7:30 AM – 6:00 PM (ET)</dd>
              </div>
            </div>
            <div className="flex gap-3" data-id="element-1965" data-mp="20rfmq47b4c18">
              <MailIcon className="h-5 w-5 text-nrs-navy mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-1966" data-mp="1vvgu24ey47v0" />
              <div data-id="element-1967" data-mp="1rtdg994t0r6k">
                <dt className="font-bold text-nrs-navy" data-id="element-1968" data-mp="1rrsnnp21vibc">Email</dt>
                <dd data-id="element-1969" data-mp="1mthcnb00r1rj">
                  <a href="mailto:help@sewp.nasa.gov" className={linkClass} data-id="element-1970" data-mp="1mv28r51zesri">
                    help@sewp.nasa.gov
                  </a>
                </dd>
              </div>
            </div>
            <div className="flex gap-3" data-id="element-1971" data-mp="7a07rd1mx6r2">
              <PhoneIcon className="h-5 w-5 text-nrs-navy mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-1972" data-mp="7d5wgr6cq6m9" />
              <div data-id="element-1973" data-mp="bak6um5rqkfu">
                <dt className="font-bold text-nrs-navy" data-id="element-1974" data-mp="gbhkclfu2qb0">Customer Help Desk</dt>
                <dd data-id="element-1975" data-mp="g9disa5eq3dw">
                  <a href="tel:+13012861478" className={linkClass} data-id="element-1976" data-mp="3fr4qhn9rjhx4">
                    (301) 286-1478
                  </a>
                </dd>
              </div>
            </div>
            <div className="flex gap-3" data-id="element-1977" data-mp="3jq3rwxwacwjt">
              <GlobeIcon className="h-5 w-5 text-nrs-navy mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-1978" data-mp="3orb4da9dijwt" />
              <div data-id="element-1979" data-mp="3tjb7tconjuy5">
                <dt className="font-bold text-nrs-navy" data-id="element-1980" data-mp="3tjuf7zgof0xx">Website</dt>
                <dd data-id="element-1981" data-mp="2w3nkjxfxfxb8">
                  <ExternalLink href="https://www.sewp.nasa.gov" data-id="element-1982" data-mp="2w245ga60bsb1">
                    NASA SEWP website
                  </ExternalLink>
                </dd>
              </div>
            </div>
          </dl>
        </div>
      </section>

      {/* H. RELATED LINKS */}
      <section className="section-light-blue" aria-labelledby="related-links" data-id="element-1983" data-mp="38x414mqn77s">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1984" data-mp="gzsgeqwg46g4">
          <SectionHeading id="related-links" data-id="element-1985" data-mp="gz0ivajafyn7">Related Links</SectionHeading>
          <ul className="space-y-3" data-id="element-1986" data-mp="czi0omiuq1o2">
            <li data-id="element-1987" data-mp="d0kqss8co02x">
              <ExternalLink href="https://www.sewp.nasa.gov/sewpvi/" data-id="element-1988" data-mp="3ldqmnbqvbu79">
                NASA SEWP VI program site
              </ExternalLink>
            </li>
            <li data-id="element-1989" data-mp="3ggi5nehghw7l">
              <Link to="/" className={linkClass} data-id="element-1990" data-mp="3gh048hrlcmio">
                New River Systems home page
              </Link>
            </li>
            <li data-id="element-1991" data-mp="3pf3t9o1sc1f8">
              <a href="https://jobs.newriversystems.com/wp-content/uploads/2026/10/New-River-Systems-SEWP-VI-Ordering-Guide.pdf" target="_blank" className={linkClass} data-id="element-1992" data-mp="3pfc3f86iwsmq">
                NRS SEWP VI Ordering Guide (PDF)
              </a>
            </li>
          </ul>
        </div>
      </section>
    </div>;
}